import { Button } from "@/components/ui/button";
import { Phone, Clock, Shield, MapPin, Award, Users } from "lucide-react";

const PHONE_NUMBER = "306-262-5995";

const HeroSection = ({ onQuoteClick }) => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center hero-bg"
    >
      {/* Overlay */}
      <div className="absolute inset-0 hero-overlay"></div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-32 text-center">
        {/* Top Badges */}
        <div className="flex flex-wrap items-center justify-center gap-4 mb-10 animate-fade-in">
          <div className="inline-flex items-center gap-3 bg-blue-900/90 border-2 border-blue-700 rounded-sm px-8 py-4">
            <Users className="w-8 h-8 text-amber-400" />
            <span className="text-white font-bold text-xl md:text-2xl uppercase tracking-wider">
              Local Family-Owned Business
            </span>
          </div>
          <div className="inline-flex items-center gap-3 bg-amber-500/30 border-2 border-amber-500 rounded-sm px-8 py-4">
            <Award className="w-8 h-8 text-amber-400" />
            <span className="text-amber-400 font-bold text-xl md:text-2xl uppercase tracking-wider">
              Fair Pricing
            </span>
          </div>
          <div className="inline-flex items-center gap-3 bg-amber-500/30 border-2 border-amber-500 rounded-sm px-8 py-4">
            <Shield className="w-8 h-8 text-amber-400" />
            <span className="text-amber-400 font-bold text-xl md:text-2xl uppercase tracking-wider">
              Best Quality
            </span>
          </div>
          <div className="inline-flex items-center gap-3 bg-amber-500/30 border-2 border-amber-500 rounded-sm px-8 py-4">
            <Clock className="w-8 h-8 text-amber-400" />
            <span className="text-amber-400 font-bold text-xl md:text-2xl uppercase tracking-wider">
              24/7 Emergency
            </span>
          </div>
        </div>

        {/* Main Heading */}
        <h1
          className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white mb-6 animate-fade-in-delay-1"
          data-testid="hero-heading"
        >
          Commercial, Residential
          <br />
          <span className="text-amber-400">& Car Locksmith Saskatoon</span>
        </h1>

        {/* Subheading */}
        <p className="text-slate-300 text-lg md:text-xl max-w-2xl mx-auto mb-4 animate-fade-in-delay-2">
          Locked out? We're Saskatoon's fastest and most trusted locksmith. 
          Residential, commercial, and automotive services — available 24/7.
        </p>

        {/* Family Trust Line */}
        <p className="text-amber-400 text-base md:text-lg max-w-xl mx-auto mb-10 animate-fade-in-delay-2 font-medium">
          Trusted by seniors & families across Saskatoon since 2014.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-delay-3">
          <a href={`tel:${PHONE_NUMBER.replace(/-/g, "")}`} data-testid="hero-call-cta">
            <Button
              size="lg"
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-lg px-8 py-6 rounded-sm pulse-button w-full sm:w-auto"
            >
              <Phone className="w-5 h-5 mr-3" />
              Call Now: {PHONE_NUMBER}
            </Button>
          </a>
          <Button
            size="lg"
            variant="outline"
            className="border-2 border-white text-white hover:bg-white hover:text-slate-900 font-semibold text-lg px-8 py-6 rounded-sm w-full sm:w-auto"
            onClick={onQuoteClick}
            data-testid="hero-quote-cta"
          >
            Request Free Quote
          </Button>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-white/70 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

const TrustBadge = ({ icon, title, description }) => (
  <div
    className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-sm p-3"
    data-testid="trust-badge"
  >
    <div className="text-amber-400">{icon}</div>
    <div className="text-left">
      <h3 className="text-white font-semibold text-sm">{title}</h3>
      <p className="text-slate-300 text-xs">{description}</p>
    </div>
  </div>
);

export default HeroSection;
